import React, { useState, useEffect, useRef } from "react";
import CategoryForm from "./CategoryForm";
import { Plus, Search } from "lucide-react";
import { categoryAPI } from "../../services";
import { toast } from "react-toastify";

import TotalCategoriesIcon from "../../assets/total-categories.svg";
import TotalProductsIcon from "../../assets/total-product.svg";
import DeleteIcon from "../../assets/delete-modal-icon.svg";
import EditIcon from "../../assets/icons/EditIcon";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon";
import NoDataIcon from "../../assets/icons/NoDataIcon";
import { useSelector } from "react-redux";

import "../../style/category.css";

const Category = () => {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats] = useState({
    total_categories: 0,
    total_products: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  // Ref to track if initial data has been loaded
  const hasLoadedInitialData = useRef(false);

  // Fetch category statistics
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await categoryAPI.getCategoryStats();
      setStats(response);
    } catch (error) {
      console.error("Error fetching category stats:", error);
      // Keep default values on error
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch categories from API
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await categoryAPI.getCategoriesList({
        search: searchTerm,
        page: currentPage,
        page_size: itemsPerPage,
      });

      setCategories(response.results || []);
      setTotalCount(response.count || 0);
      setTotalPages(Math.ceil((response.count || 0) / itemsPerPage));
    } catch (error) {
      console.error("Error fetching categories:", error);
      // Fallback to localStorage for offline functionality
      const stored = localStorage.getItem("categories");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setCategories(parsed);
          setTotalCount(parsed.length);
          setTotalPages(Math.ceil(parsed.length / itemsPerPage));
        } catch {
          setCategories([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Load stats and categories on component mount only (once, even with StrictMode)
  useEffect(() => {
    if (!hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      fetchStats();
      fetchCategories();
    }
  }, []);

  // Fetch categories when page changes (skip initial mount)
  useEffect(() => {
    if (hasLoadedInitialData.current && currentPage !== 1) {
      fetchCategories();
    }
  }, [currentPage]);

  // Debounce search - reset to page 1 and fetch (skip initial mount)
  useEffect(() => {
    if (hasLoadedInitialData.current && searchTerm !== "") {
      const timeoutId = setTimeout(() => {
        if (currentPage === 1) {
          fetchCategories();
        } else {
          setCurrentPage(1);
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  const handleAddCategory = async (data) => {
    try {
      setLoading(true);
      await categoryAPI.createCategory(data);
      await fetchCategories(); // Refresh the list
      await fetchStats(); // Refresh the stats
      setShowForm(false);
      toast.success("Category added successfully!");
    } catch (error) {
      console.error("Error adding category:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to add category. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = async (data) => {
    try {
      setLoading(true);
      await categoryAPI.updateCategory(editingCategory.id, data);
      await fetchCategories(); // Refresh the list
      await fetchStats(); // Refresh the stats
      setShowForm(false);
      setEditingCategory(null);
      toast.success("Category updated successfully!");
    } catch (error) {
      console.error("Error updating category:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update category. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      setLoading(true);
      await categoryAPI.deleteCategory(id);
      await fetchCategories(); // Refresh the list
      await fetchStats(); // Refresh the stats
      setShowDeleteModal(false);
      setDeletingCategory(null);
      toast.success("Category deleted successfully!");
    } catch (error) {
      console.error("Error deleting category:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to delete category. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openEditForm = (category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Generate avatar color based on category name
  const getAvatarColor = (name) => {
    const colors = [
      "#3b82f6", // blue
      "#ef4444", // red
      "#10b981", // green
      "#f59e0b", // yellow
      "#8b5cf6", // purple
      "#06b6d4", // cyan
      "#f97316", // orange
      "#84cc16", // lime
      "#ec4899", // pink
      "#6366f1", // indigo
    ];

    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className="card rounded-4 p-4 border-0 category-container category-layout">
      <div className="category-top">
        {/* header */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h2 className="mrph-txt size-22 weight-600 color-text-medium mb-1">
              Category
            </h2>
            <p className="mrph-txt size-14 weight-400 color-text-semi-regular">
              Manage your category
            </p>
          </div>
          <button
            className={`${
              isPartsURL
                ? "add-brand-btn-parts"
                : isAccessoriesURL
                ? "add-brand-btn-acc"
                : "add-brand-btn"
            }`}
            onClick={() => setShowForm(true)}
          >
            <Plus className="add-brand-icon" /> Add Category
          </button>
        </div>

        {/* stats */}
        <div className="row mb-4">
          <div className="col-md-4">
            <div
              className="card mrph-border border brands-summary-card"
              style={{ borderRadius: "10px", height: "92px" }}
            >
              <div className="card-body d-flex align-items-start justify-content-between">
                <div className="d-flex flex-column align-items-start justify-content-center gap-2">
                  <h6 className="mrph-txt size-14 weight-400 color-text-semi-regular mb-0">
                    Total Categories
                  </h6>
                  <h4 className="mrph-txt size-22 weight-600 color-text-medium mb-0">
                    {statsLoading ? (
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      stats.total_categories
                    )}
                  </h4>
                </div>
                <img src={TotalCategoriesIcon} alt="Total Categories" />
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div
              className="card mrph-border border brands-summary-card"
              style={{ borderRadius: "10px", height: "92px" }}
            >
              <div className="card-body d-flex align-items-start justify-content-between">
                <div className="d-flex flex-column align-items-start justify-content-center gap-2">
                  <h6 className="mrph-txt size-14 weight-400 color-text-semi-regular mb-0">
                    Total Products
                  </h6>
                  <h4 className="mrph-txt size-22 weight-600 color-text-medium mb-0">
                    {statsLoading ? (
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      stats.total_products
                    )}
                  </h4>
                </div>
                <img src={TotalProductsIcon} alt="Total Products" />
              </div>
            </div>
          </div>
        </div>

        {/* search/filter */}
        <div className="d-flex align-items-center mb-3 gap-3">
          <div className="position-relative flex-grow-1">
            <input
              type="text"
              className={`${
                isPartsURL
                  ? "brand-search-input-parts"
                  : isAccessoriesURL
                  ? "brand-search-input-acc"
                  : "brand-search-input"
              } text-search`}
              placeholder="Search categories by name or description"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search
              size={16}
              className="position-absolute top-50 start-0 translate-middle-y ms-3"
              style={{ color: "#8C959F" }}
            />
          </div>
        </div>
      </div>

      {/* table */}
      <div className="category-table-container category-table-wrapper">
        <table className="category-table">
          <thead style={{ backgroundColor: "#F6F8FA" }}>
            <tr>
              <th>CATEGORY NAME</th>
              <th>DESCRIPTION</th>
              <th># PRODUCTS</th>
              <th style={{ textAlign: "center" }}>ACTION</th>
            </tr>
          </thead>
          <tbody className="category-tbody">
            {loading ? (
              <tr>
                <td colSpan="4" className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : categories.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-products-message">
                  <div
                    className=""
                    style={{
                      marginBottom: "4rem",
                      marginTop: "4rem",
                    }}
                  >
                    <NoDataIcon />
                    <h3 style={{ marginTop: "2rem" }}>No categories found</h3>
                  </div>
                </td>
              </tr>
            ) : (
              categories.map((category) => (
                <tr
                  key={category.id}
                  className="mrph-txt size-14 color-text-regular"
                  style={{ height: "60px" }}
                >
                  <td className="px-3">
                    <div className="d-flex align-items-center gap-3">
                      {category.image ? (
                        <img
                          src={category.image}
                          alt={category.name}
                          className="category-table-image"
                          style={{
                            width: "45px",
                            height: "45px",
                            // objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          className="category-avatar"
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "50%",
                            backgroundColor: getAvatarColor(category.name),
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            border: "2px solid #f1f5f9",
                            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          {category.name.charAt(0)}
                        </div>
                      )}
                      <span>{category.name}</span>
                    </div>
                  </td>
                  <td className="px-3">
                    <span
                      className="category-description-text"
                      title={category.description}
                    >
                      {category.description}
                    </span>
                  </td>
                  <td className="px-3">{category.product_count || 0}</td>

                  <td>
                    <div className="action-buttons">
                      <button
                        className={`${isPartsURL ? "action-btn-parts view" : isAccessoriesURL ? "action-btn-acc view" : "action-btn view"}`}
                        onClick={() => openEditForm(category)}
                        title="Edit"
                      >
                        <EditIcon fill={isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)" : "var(--primary-color-mobile)"} />
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => {
                          setDeletingCategory(category);
                          setShowDeleteModal(true);
                        }}
                        title="Delete"
                      >
                        <RedDeleteIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4 category-pagination-sticky">
          <div className="mrph-txt size-14 color-text-semi-regular">
          Page {currentPage} of{" "}
          {Math.ceil(categories.length / itemsPerPage)}
          </div>
          <div className="pagination-controls">
            <button
              className={`${
                isPartsURL
                  ? "pagination-btn-parts"
                  : isAccessoriesURL
                  ? "pagination-btn-acc"
                  : "pagination-btn"
              }`}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            >
              Previous
            </button>
            <button
              className={`${
                isPartsURL
                  ? "pagination-btn-next-parts"
                  : isAccessoriesURL
                  ? "pagination-btn-next-acc"
                  : "pagination-btn-next"
              }`}
              disabled={
                currentPage >= Math.ceil(categories.length / itemsPerPage)
              }
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(
                    Math.ceil(categories.length / itemsPerPage),
                    prev + 1
                  )
                )
              }
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* form modal */}
      {showForm && (
        <CategoryForm
          category={editingCategory}
          onSave={editingCategory ? handleEditCategory : handleAddCategory}
          onCancel={() => {
            setShowForm(false);
            setEditingCategory(null);
          }}
        />
      )}

      {/* delete modal */}
      {showDeleteModal && deletingCategory && (
        <>
          <div className="modal-overlay">
            <div className="delete-modal">
              <div className="modal-icon">
                <img src={DeleteIcon} alt="Delete" />
              </div>
              <h3 className="modal-title">Delete Category</h3>
              <p className="modal-message">
                Are you sure you want to delete category{" "}
                <b>{deletingCategory.name}</b>?
                <br />
                This action cannot be undone.
              </p>
              <div className="modal-actions">
                <button
                  className="modal-cancel-btn"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="modal-delete-btn"
                  onClick={() => handleDeleteCategory(deletingCategory.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Category;
